/**
 * What each API-mode tool does in the app (see apiChat.ts for the tools themselves). Reads go to the
 * same API the screens use and to the repository's files on this machine; changes go through
 * `runAction` (localChat.ts), the same code the CLI chat's action cards run.
 */

import { api } from './api'
import { checkAction, runAction, type ActionContext } from './localChat'
import { repoFiles, repoRead, repoWrite } from './desktop'

export interface ToolContext extends ActionContext {
  repositoryId: string | null
  /** The active repository's folder on this machine, when it is here. */
  folder: string | null
}

const MAX_READ = 60_000

const json = (value: unknown) => JSON.stringify(value, null, 1).slice(0, MAX_READ)

/** A path inside the repository: relative, no parent references, no drive letters. */
function safePath(path: unknown): string {
  const p = typeof path === 'string' ? path.trim().replace(/\\/g, '/') : ''
  if (!p || p.startsWith('/') || /^[a-z]:/i.test(p) || p.split('/').includes('..')) throw new Error('That path is not inside the repository.')
  return p
}

export function makeToolRunner(ctx: ToolContext) {
  return async (name: string, input: Record<string, unknown>): Promise<string> => {
    switch (name) {
      case 'list_work_orders': {
        const orders = await api.workOrders.list()
        return json(orders.slice(0, 30).map((o) => ({ id: o.id, kind: o.kind, status: o.status, phase: o.phase, round: `${o.iteration}/${o.maxIterations}`, title: o.title, repository: o.repositoryName, endReason: o.endReason })))
      }
      case 'list_queue': {
        const queue = await api.promptQueue.list()
        return json({ desktopOnline: queue.desktopOnline, items: queue.items.slice(0, 20).map((i) => ({ id: i.id, status: i.status, prompt: i.prompt.slice(0, 200), repository: i.repository })) })
      }
      case 'list_repositories': {
        const repos = await api.repositories.list()
        return json(repos.filter((r) => r.linked).map((r) => ({ id: r.id, name: r.name, project: r.projectKey, team: r.teamName, remote: r.remoteUrl })))
      }
      case 'list_teammates':
        return json((await api.workOrders.teammates(ctx.repositoryId)).map((m) => ({ name: m.name, me: m.isMe, acceptsTeamWork: m.acceptsTeamWork, reachable: m.remoteOn || Boolean(m.desktopOn) })))
      case 'list_live_sessions':
        return json((await api.dashboard.live()).filter((s) => !s.isTerminal).map((s) => ({ who: s.developerName, repository: s.repositoryName, branch: s.branch, step: s.currentStepTitle, progress: s.progressPercent })))
      case 'search_docs':
        return json(await api.docs.search(String(input.query ?? '')))
      case 'read_doc': {
        const doc = await api.docs.byPath(String(input.path ?? ''))
        return `# ${doc.path} (version ${doc.version})\n\n${doc.content.slice(0, MAX_READ)}`
      }
      case 'list_files': {
        if (!ctx.folder) return 'The active repository is not on this machine.'
        const under = typeof input.under === 'string' ? input.under.trim().replace(/\\/g, '/') : ''
        const { files, branch } = await repoFiles(ctx.folder)
        const listed = files.filter((f) => !under || f.startsWith(under))
        return `branch ${branch ?? '?'}, ${listed.length} files\n${listed.slice(0, 2000).join('\n')}`
      }
      case 'read_file': {
        if (!ctx.folder) return 'The active repository is not on this machine.'
        return (await repoRead(ctx.folder, safePath(input.path))).slice(0, MAX_READ)
      }
      case 'write_file': {
        if (!ctx.folder) return 'The active repository is not on this machine.'
        const path = safePath(input.path)
        await repoWrite(ctx.folder, path, String(input.content ?? ''))
        return `Wrote ${path}.`
      }
      case 'open_screen':
      case 'create_work_order':
      case 'start_team_work':
      case 'queue_prompt':
      case 'write_doc':
      case 'pin_doc':
      case 'run_audit': {
        const action = checkAction(JSON.stringify(toAction(name, input)))
        if (!action) return 'Those arguments are not valid for this tool.'
        return runAction(action, ctx)
      }
      default:
        return `No tool called ${name}.`
    }
  }
}

/** The CLI chat's action for a tool call — one validation and one runner for both modes. */
function toAction(name: string, input: Record<string, unknown>): Record<string, unknown> {
  switch (name) {
    case 'open_screen':
      return { type: 'open', route: input.route }
    case 'create_work_order':
      return { type: 'work_order', kind: input.kind, instructions: input.instructions, intervalMinutes: input.interval_minutes }
    case 'start_team_work':
      return { type: 'team', instructions: input.instructions, builder: input.builder || undefined, reviewer: input.reviewer || undefined }
    case 'queue_prompt':
      return { type: 'queue', instructions: input.instructions }
    case 'write_doc':
      return { type: 'doc', path: input.path, content: input.content }
    case 'pin_doc':
      return { type: 'pin', path: input.path }
    default:
      return { type: 'audit', key: input.key }
  }
}
